import { useParams, useNavigate } from "react-router-dom";
import { blogPosts } from "@/mocks/blog";
import type { BlogSection } from "@/mocks/blog";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

// Generate Schema.org Article structured data
function generateSchemaOrgArticle(post: typeof blogPosts[0]) {
  const datePublished = new Date(post.publishedAt).toISOString();
  const dateModified = datePublished;

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription || post.excerpt,
    image: post.image,
    datePublished,
    dateModified,
    author: {
      "@type": "Person",
      name: post.author,
      jobTitle: post.authorRole,
    },
    publisher: {
      "@type": "Organization",
      name: "SolarLeadSource",
      logo: {
        "@type": "ImageObject",
        url: "https://solarleadsource.com/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://solarleadsource.com/blog/${post.slug}`,
    },
    articleSection: post.category,
    keywords: post.tags.join(", "),
    wordCount: post.content.reduce((acc, section) => {
      if (section.text) return acc + section.text.split(" ").length;
      if (section.items) return acc + section.items.join(" ").split(" ").length;
      return acc;
    }, 0),
  };
}

function renderSection(section: BlogSection, idx: number) {
  switch (section.type) {
    case "heading":
      return (
        <h2 key={idx} className="text-xl font-bold text-gray-900 mt-10 mb-3">
          {section.text}
        </h2>
      );
    case "subheading":
      return (
        <h3 key={idx} className="text-lg font-semibold text-gray-800 mt-7 mb-2">
          {section.text}
        </h3>
      );
    case "paragraph":
      return (
        <p key={idx} className="text-gray-600 leading-relaxed text-[15px] mb-4">
          {section.text}
        </p>
      );
    case "list":
      return (
        <ul key={idx} className="space-y-3 mb-6">
          {section.items?.map((item, i) => (
            <li key={i} className="flex gap-3 text-[15px] text-gray-600 leading-relaxed">
              <span className="mt-1.5 w-4 h-4 flex-shrink-0 flex items-center justify-center">
                <span className="w-1.5 h-1.5 rounded-full bg-[#16a34a]"></span>
              </span>
              {item}
            </li>
          ))}
        </ul>
      );
    case "callout":
      return (
        <div key={idx} className="my-6 bg-[#f0fdf4] border-l-4 border-[#16a34a] rounded-r-xl p-5">
          <div className="flex gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-lightbulb-line text-[#16a34a] text-base"></i>
            </div>
            <p className="text-gray-700 text-sm leading-relaxed">{section.text}</p>
          </div>
        </div>
      );
    case "stat-grid":
      return (
        <div key={idx} className="my-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {section.stats?.map((stat, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-[#16a34a] mb-1">{stat.value}</p>
              <p className="text-xs text-gray-500 leading-snug">{stat.label}</p>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const post = blogPosts.find((p) => p.slug === slug);
  const related = blogPosts.filter((p) => p.slug !== slug && p.category === post?.category).slice(0, 3);

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <p className="text-gray-500 text-lg mb-4">Article not found</p>
        <button
          onClick={() => navigate("/blog")}
          className="text-[#16a34a] font-medium hover:underline cursor-pointer"
        >
          Back to Blog
        </button>
      </div>
    );
  }

  const schemaOrgData = generateSchemaOrgArticle(post);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* SEO Meta Tags */}
      <title>{post.title} | SolarLeadSource Blog</title>
      <meta name="description" content={post.metaDescription} />
      <meta name="keywords" content={post.tags.join(", ")} />
      <link rel="canonical" href={`https://solarleadsource.com/blog/${post.slug}`} />
      
      {/* Open Graph */}
      <meta property="og:title" content={post.title} />
      <meta property="og:description" content={post.metaDescription} />
      <meta property="og:image" content={post.image} />
      <meta property="og:type" content="article" />
      <meta property="article:published_time" content={new Date(post.publishedAt).toISOString()} />
      <meta property="article:author" content={post.author} />
      <meta property="article:section" content={post.category} />
      {post.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      
      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrgData) }}
      />
      
      <Navbar onRequestLeads={() => {}} />

      {/* Hero Image */}
      <div className="pt-16">
        <div className="relative h-64 md:h-80 overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row gap-12 py-10">
          {/* Article */}
          <article className="flex-1 min-w-0">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <button onClick={() => navigate("/")} className="hover:text-gray-600 cursor-pointer">Home</button>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <button onClick={() => navigate("/blog")} className="hover:text-gray-600 cursor-pointer">Blog</button>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-gray-600 truncate">{post.category}</span>
            </nav>

            {/* Category + Meta */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="bg-[#f0fdf4] text-[#16a34a] text-xs font-semibold px-3 py-1 rounded-full">
                {post.category}
              </span>
              <span className="text-gray-400 text-xs flex items-center gap-1">
                <i className="ri-time-line"></i> {post.readTime}
              </span>
              <span className="text-gray-400 text-xs flex items-center gap-1">
                <i className="ri-calendar-line"></i> {post.publishedAt}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-6">
              {post.title}
            </h1>

            {/* Author */}
            <div className="flex items-center gap-3 pb-6 border-b border-gray-100 mb-8">
              <div className={`w-10 h-10 ${post.authorColor} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                {post.authorInitials}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">{post.author}</p>
                <p className="text-xs text-gray-400">{post.authorRole}</p>
              </div>
            </div>

            {/* Excerpt */}
            <p className="text-lg text-gray-500 leading-relaxed mb-8 font-medium border-l-4 border-gray-200 pl-4">
              {post.excerpt}
            </p>

            {/* Content */}
            <div>
              {post.content.map((section, idx) => renderSection(section, idx))}
            </div>

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-gray-100">
              <p className="text-xs text-gray-400 uppercase tracking-widest mb-3">Tags</p>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-gray-100 text-gray-600 text-xs px-3 py-1.5 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-10 bg-[#f0fdf4] rounded-2xl p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#16a34a] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-sun-line text-white text-base"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Ready to put this into practice?</h3>
                  <p className="text-gray-500 text-sm mb-4">
                    Browse exclusive, scored solar leads in your service states — no contracts, no minimums.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => navigate("/signup")}
                      className="bg-[#16a34a] text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-[#15803d] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Get Started Free
                    </button>
                    <button
                      onClick={() => navigate("/pricing")}
                      className="bg-white text-gray-700 text-sm font-semibold px-5 py-2.5 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      View Pricing
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="w-full lg:w-72 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              {/* Back to Blog */}
              <button
                onClick={() => navigate("/blog")}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer group"
              >
                <i className="ri-arrow-left-line group-hover:-translate-x-0.5 transition-transform"></i>
                Back to all articles
              </button>

              {/* Related Articles */}
              {related.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
                    Related Articles
                  </h4>
                  <div className="space-y-4">
                    {related.map((rel) => (
                      <div
                        key={rel.slug}
                        onClick={() => navigate(`/blog/${rel.slug}`)}
                        className="group cursor-pointer"
                      >
                        <div className="flex gap-3">
                          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                            <img
                              src={rel.image}
                              alt={rel.title}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 leading-snug group-hover:text-[#16a34a] transition-colors line-clamp-2">
                              {rel.title}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">{rel.readTime}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All Posts from same category */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                  More in {post.category}
                </h4>
                <button
                  onClick={() => navigate("/blog")}
                  className="text-sm text-[#16a34a] font-medium hover:underline cursor-pointer"
                >
                  Browse all {post.category} articles →
                </button>
              </div>

              {/* Stats */}
              <div className="bg-[#f0fdf4] rounded-xl p-4 space-y-3">
                <h4 className="text-xs font-semibold text-[#16a34a] uppercase tracking-widest">
                  SolarLeadSource
                </h4>
                {[
                  { icon: "ri-shield-check-line", text: "100% exclusive leads" },
                  { icon: "ri-map-pin-line", text: "Northeast US coverage" },
                  { icon: "ri-bar-chart-line", text: "Scored 50–99 range" },
                  { icon: "ri-time-line", text: "5-min reservation window" },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${item.icon} text-[#16a34a] text-xs`}></i>
                    </div>
                    {item.text}
                  </div>
                ))}
                <button
                  onClick={() => navigate("/signup")}
                  className="w-full mt-2 bg-[#16a34a] text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-[#15803d] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get Started Free
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}
